import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { excerpt } from '../../utils/richtext';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { localizedText } from '../../i18n/contentLocales';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditIcon from '@mui/icons-material/Edit';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlaceIcon from '@mui/icons-material/Place';
import ScheduleIcon from '@mui/icons-material/Schedule';
import {
  DndContext,
  DragOverlay,
  type DragEndEvent,
  type DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  deleteRallye,
  duplicateRallye,
  getRallye,
  importChallengeToRallyeStation,
  importStation,
  listRallyeStationChallenges,
  listRallyeStations,
  reorderRallyeStationChallenges,
  reorderStations,
  unlinkRallyeStationChallenge,
  unlinkStation,
  updateRallye,
} from '../../api/content';
import { ApiError } from '../../api/client';
import { useAuth } from '../../auth/useAuth';
import type {
  MultilingualString,
  Rallye,
  RallyeStationChallengeLink,
  RallyeStationLink,
} from '../../api/types';
import ConfirmDialog from '../../components/ConfirmDialog';
import ErrorAlert from '../../components/ErrorAlert';
import LoadingSpinner from '../../components/LoadingSpinner';
import MultilingualField from '../../components/MultilingualField';
import BackLink from '../../components/BackLink';
import AddChoiceDialog from '../../components/AddChoiceDialog';
import ImportStationDialog from '../../components/ImportStationDialog';
import ImportChallengeDialog from '../../components/ImportChallengeDialog';

type DialogKind = 'delete' | 'duplicate' | null;

// --------------------------------------------------------------------------
// Challenge row (inside an expanded station): drag handle + remove.
// --------------------------------------------------------------------------
interface SortableChallengeRowProps {
  link: RallyeStationChallengeLink;
  index: number;
  label: string;
  typeLabel: string;
  metrics?: string;
  onRemove: () => void;
  dragHandleLabel: string;
  removeLabel: string;
}

const SortableChallengeRow: React.FC<SortableChallengeRowProps> = ({
  link,
  index,
  label,
  typeLabel,
  metrics,
  onRemove,
  dragHandleLabel,
  removeLabel,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: link.challenge.id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <Box
      ref={setNodeRef}
      style={style}
      aria-roledescription="sortable item"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        pl: 4,
        pr: 1,
        py: 1,
        borderTop: 1,
        borderColor: 'divider',
        '&:hover': { bgcolor: 'action.selected' },
      }}
    >
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography variant="body2" noWrap>
          {`${index + 1}. ${label}`}
        </Typography>
        {metrics && (
          <Typography variant="caption" color="text.secondary">
            {metrics}
          </Typography>
        )}
      </Box>
      <Chip label={typeLabel} size="small" />
      <IconButton aria-label={removeLabel} color="error" onClick={onRemove}>
        <DeleteIcon fontSize="small" />
      </IconButton>
      <IconButton
        aria-label={dragHandleLabel}
        {...attributes}
        {...listeners}
        sx={{ cursor: 'grab', touchAction: 'none' }}
      >
        <DragIndicatorIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

// --------------------------------------------------------------------------
// Station row: expandable; carries its own challenge list/dnd.
// --------------------------------------------------------------------------
interface SortableStationRowProps {
  link: RallyeStationLink;
  index: number;
  label: string;
  secondary?: string;
  expanded: boolean;
  onToggle: () => void;
  onOpen: () => void;
  onRemove: () => void;
  onAddChallenge: () => void;
  dragHandleLabel: string;
  removeLabel: string;
  expandLabel: string;
  children: React.ReactNode;
}

// Presentational station header (badge + name/address + metric columns).
// Shared by the sortable row and the drag overlay so the drag preview keeps
// the exact same look and size.
const StationHeaderContent: React.FC<{
  link: RallyeStationLink;
  index: number;
  label: string;
  secondary?: string;
}> = ({ link, index, label, secondary }) => {
  const { t } = useTranslation('content');
  const metric = (labelText: string, value: number, icon?: React.ReactNode) => (
    <Stack spacing={0.25} sx={{ alignItems: 'center', minWidth: 72 }}>
      <Typography variant="caption" color="text.secondary" noWrap>
        {labelText}
      </Typography>
      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
        {icon}
        <Typography variant="h6" component="span" sx={{ fontWeight: 600, lineHeight: 1 }}>
          {value}
        </Typography>
      </Stack>
    </Stack>
  );
  return (
    <>
      <Box
        sx={{
          flexShrink: 0,
          width: 32,
          height: 32,
          borderRadius: '50%',
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 600,
          fontSize: '0.95rem',
        }}
      >
        {index + 1}
      </Box>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography variant="subtitle1" noWrap sx={{ fontWeight: 500 }}>
          {label}
        </Typography>
        {secondary && (
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', minWidth: 0 }}>
            <PlaceIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
            <Typography variant="body2" color="text.secondary" noWrap>
              {secondary}
            </Typography>
          </Stack>
        )}
      </Box>
      <Stack
        direction="row"
        spacing={3}
        sx={{ display: { xs: 'none', sm: 'flex' }, flexShrink: 0 }}
      >
        {metric(t('composition.colChallenges'), link.challenge_count)}
        {metric(
          t('composition.colPoints'),
          link.total_points,
          <EmojiEventsOutlinedIcon sx={{ fontSize: 18, color: 'primary.main' }} />,
        )}
        {metric(
          t('composition.colTime'),
          link.total_duration_minutes,
          <ScheduleIcon sx={{ fontSize: 18, color: 'primary.main' }} />,
        )}
      </Stack>
    </>
  );
};

const SortableStationRow: React.FC<SortableStationRowProps> = ({
  link,
  index,
  label,
  secondary,
  expanded,
  onToggle,
  onOpen,
  onRemove,
  onAddChallenge,
  dragHandleLabel,
  removeLabel,
  expandLabel,
  children,
}) => {
  const { t } = useTranslation('content');
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: link.station.id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  // While this row is the one being dragged it is hidden (opacity 0) but keeps
  // its real height as the placeholder; a faithful clone is rendered in the
  // DragOverlay, so the layout doesn't shift and nothing squashes/stretches.
  return (
    <Card
      ref={setNodeRef}
      style={style}
      variant="outlined"
      sx={{ mb: 1.5, borderRadius: 2, opacity: isDragging ? 0 : 1 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1.5 }}>
        <StationHeaderContent link={link} index={index} label={label} secondary={secondary} />
        <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
          <IconButton aria-label={removeLabel} color="error" onClick={onRemove}>
            <DeleteIcon fontSize="small" />
          </IconButton>
          <IconButton aria-label={t('composition.editStation')} onClick={onOpen}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton aria-label={expandLabel} aria-expanded={expanded} onClick={onToggle}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
          <IconButton
            aria-label={dragHandleLabel}
            {...attributes}
            {...listeners}
            sx={{ cursor: 'grab', touchAction: 'none' }}
          >
            <DragIndicatorIcon />
          </IconButton>
        </Stack>
      </Box>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ pb: 1, bgcolor: 'action.hover' }}>
          {children}
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={onAddChallenge}
            sx={{ mt: 1, ml: 4 }}
          >
            {t('composition.addChallenge')}
          </Button>
        </Box>
      </Collapse>
    </Card>
  );
};

const RallyeDetail: React.FC = () => {
  const { rallyeId } = useParams<{ rallyeId: string }>();
  const { user } = useAuth();
  const { t, i18n } = useTranslation('content');
  const navigate = useNavigate();

  const [rallye, setRallye] = useState<Rallye | null>(null);
  const [links, setLinks] = useState<RallyeStationLink[]>([]);
  const [title, setTitle] = useState<MultilingualString>({});
  const [description, setDescription] = useState<MultilingualString>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [togglingPublish, setTogglingPublish] = useState(false);
  const [dialog, setDialog] = useState<DialogKind>(null);
  const [dialogBusy, setDialogBusy] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [activeStationId, setActiveStationId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [toUnlink, setToUnlink] = useState<RallyeStationLink | null>(null);
  const [unlinking, setUnlinking] = useState(false);

  // Per-station challenge state, keyed by the rallye-station link_id.
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [challenges, setChallenges] = useState<Record<string, RallyeStationChallengeLink[]>>({});
  const [reorderingChallenges, setReorderingChallenges] = useState<Record<string, boolean>>({});
  // The rallye-station currently importing/adding a challenge.
  const [addChallengeFor, setAddChallengeFor] = useState<RallyeStationLink | null>(null);
  const [importChallengeFor, setImportChallengeFor] = useState<RallyeStationLink | null>(null);
  const [toUnlinkChallenge, setToUnlinkChallenge] = useState<{
    station: RallyeStationLink;
    challenge: RallyeStationChallengeLink;
  } | null>(null);
  const [unlinkingChallenge, setUnlinkingChallenge] = useState(false);

  const orgSlug = user?.organization_slug;
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const activeLang = i18n.language.startsWith('de') ? 'de' : 'en';
  const titleOf = (ml: MultilingualString) => localizedText(ml, activeLang);

  const load = useCallback(async () => {
    if (!orgSlug || !rallyeId) return;
    setLoading(true);
    setError(null);
    try {
      const [r, s] = await Promise.all([
        getRallye(orgSlug, rallyeId),
        listRallyeStations(orgSlug, rallyeId),
      ]);
      setRallye(r);
      setTitle(r.title);
      setDescription(r.description);
      setLinks([...s.items].sort((a, b) => a.sort_order - b.sort_order));
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
    } finally {
      setLoading(false);
    }
  }, [orgSlug, rallyeId]);

  useEffect(() => {
    void load();
    setDialog(null);
    setDialogBusy(false);
  }, [load]);

  const metrics = useMemo(() => {
    let challengeCount = 0;
    let points = 0;
    let duration = 0;
    for (const l of links) {
      challengeCount += l.challenge_count;
      points += l.total_points;
      duration += l.total_duration_minutes;
    }
    return { stations: links.length, challenges: challengeCount, points, duration };
  }, [links]);

  const isDirty =
    !!rallye &&
    (JSON.stringify(title) !== JSON.stringify(rallye.title) ||
      JSON.stringify(description) !== JSON.stringify(rallye.description));
  const deTitleMissing = !title.de?.trim();

  const loadChallenges = useCallback(
    async (link: RallyeStationLink) => {
      if (!orgSlug || !rallyeId) return;
      try {
        const resp = await listRallyeStationChallenges(orgSlug, rallyeId, link.link_id);
        setChallenges((prev) => ({
          ...prev,
          [link.link_id]: [...resp.items].sort((a, b) => a.sort_order - b.sort_order),
        }));
      } catch (err) {
        setError(err instanceof ApiError ? err.detail : String(err));
      }
    },
    [orgSlug, rallyeId],
  );

  const handleToggleExpand = (link: RallyeStationLink) => {
    const next = !expanded[link.link_id];
    setExpanded((prev) => ({ ...prev, [link.link_id]: next }));
    if (next && challenges[link.link_id] === undefined) {
      void loadChallenges(link);
    }
  };

  const handleSave = async () => {
    if (!orgSlug || !rallyeId || deTitleMissing || !rallye) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await updateRallye(orgSlug, rallyeId, {
        title,
        description,
        is_published: rallye.is_published,
      });
      setRallye(updated);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePublish = async () => {
    if (!orgSlug || !rallyeId || !rallye) return;
    setTogglingPublish(true);
    setError(null);
    try {
      const updated = await updateRallye(orgSlug, rallyeId, {
        title: rallye.title,
        description: rallye.description,
        is_published: !rallye.is_published,
      });
      setRallye(updated);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
    } finally {
      setTogglingPublish(false);
    }
  };

  const handleDelete = async () => {
    if (!orgSlug || !rallyeId) return;
    setDialogBusy(true);
    try {
      await deleteRallye(orgSlug, rallyeId);
      navigate('/rallyes');
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
      setDialogBusy(false);
      setDialog(null);
    }
  };

  const handleDuplicate = async () => {
    if (!orgSlug || !rallyeId) return;
    setDialogBusy(true);
    try {
      const copy = await duplicateRallye(orgSlug, rallyeId);
      navigate(`/rallyes/${copy.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
      setDialogBusy(false);
      setDialog(null);
    }
  };

  const handleImportStation = async (stationId: string) => {
    if (!orgSlug || !rallyeId) return;
    await importStation(orgSlug, rallyeId, { station_id: stationId });
    setImportOpen(false);
    await load();
  };

  const handleUnlink = async () => {
    if (!orgSlug || !rallyeId || !toUnlink) return;
    setUnlinking(true);
    try {
      await unlinkStation(orgSlug, rallyeId, toUnlink.station.id);
      setLinks((prev) => prev.filter((l) => l.link_id !== toUnlink.link_id));
      setToUnlink(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
    } finally {
      setUnlinking(false);
    }
  };

  const handleImportChallenge = async (challengeId: string) => {
    if (!orgSlug || !rallyeId || !importChallengeFor) return;
    const target = importChallengeFor;
    await importChallengeToRallyeStation(orgSlug, rallyeId, target.link_id, {
      challenge_id: challengeId,
    });
    setImportChallengeFor(null);
    await loadChallenges(target);
    await load();
  };

  const handleUnlinkChallenge = async () => {
    if (!orgSlug || !rallyeId || !toUnlinkChallenge) return;
    const { station, challenge } = toUnlinkChallenge;
    setUnlinkingChallenge(true);
    try {
      await unlinkRallyeStationChallenge(orgSlug, rallyeId, station.link_id, challenge.challenge.id);
      setChallenges((prev) => ({
        ...prev,
        [station.link_id]: (prev[station.link_id] ?? []).filter(
          (l) => l.link_id !== challenge.link_id,
        ),
      }));
      setToUnlinkChallenge(null);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
    } finally {
      setUnlinkingChallenge(false);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    setActiveStationId(null);
    const { active, over } = event;
    if (!over || active.id === over.id || !orgSlug || !rallyeId) return;
    const ids = links.map((l) => l.station.id);
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from === -1 || to === -1) return;
    const nextIds = [...ids];
    nextIds.splice(from, 1);
    nextIds.splice(to, 0, String(active.id));

    const prev = links;
    const reordered = nextIds
      .map((id) => links.find((l) => l.station.id === id))
      .filter((l): l is RallyeStationLink => !!l);
    setLinks(reordered);
    setReordering(true);
    setError(null);
    try {
      const resp = await reorderStations(orgSlug, rallyeId, nextIds);
      setLinks([...resp.items].sort((a, b) => a.sort_order - b.sort_order));
    } catch (err) {
      setLinks(prev);
      setError(err instanceof ApiError ? err.detail : String(err));
    } finally {
      setReordering(false);
    }
  };

  const handleChallengeDragEnd = async (link: RallyeStationLink, event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !orgSlug || !rallyeId) return;
    const current = challenges[link.link_id] ?? [];
    const ids = current.map((l) => l.challenge.id);
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from === -1 || to === -1) return;
    const nextIds = [...ids];
    nextIds.splice(from, 1);
    nextIds.splice(to, 0, String(active.id));

    const reordered = nextIds
      .map((id) => current.find((l) => l.challenge.id === id))
      .filter((l): l is RallyeStationChallengeLink => !!l);
    setChallenges((prev) => ({ ...prev, [link.link_id]: reordered }));
    setReorderingChallenges((prev) => ({ ...prev, [link.link_id]: true }));
    setError(null);
    try {
      const resp = await reorderRallyeStationChallenges(orgSlug, rallyeId, link.link_id, nextIds);
      setChallenges((prev) => ({
        ...prev,
        [link.link_id]: [...resp.items].sort((a, b) => a.sort_order - b.sort_order),
      }));
    } catch (err) {
      setChallenges((prev) => ({ ...prev, [link.link_id]: current }));
      setError(err instanceof ApiError ? err.detail : String(err));
    } finally {
      setReorderingChallenges((prev) => ({ ...prev, [link.link_id]: false }));
    }
  };

  if (loading) return <LoadingSpinner />;
  if (!rallye) {
    return (
      <Box>
        <ErrorAlert message={error ?? 'Rallye not found'} onRetry={load} />
      </Box>
    );
  }

  return (
    <Box>
      <BackLink to="/rallyes" label={t('rallye.detail.backToList')} />
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{ justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, mb: 3 }}
      >
        <Box>
          <Typography variant="h2" component="h1">
            {titleOf(rallye.title) || t('rallye.detail.title')}
          </Typography>
          <Chip
            label={
              rallye.is_published ? t('rallye.list.filter.published') : t('rallye.list.filter.draft')
            }
            color={rallye.is_published ? 'success' : 'default'}
            size="small"
            sx={{ mt: 1 }}
          />
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            startIcon={<ContentCopyIcon />}
            variant="outlined"
            onClick={() => setDialog('duplicate')}
          >
            {t('rallye.detail.duplicate')}
          </Button>
          <Button
            startIcon={<DeleteIcon />}
            color="error"
            variant="outlined"
            onClick={() => setDialog('delete')}
          >
            {t('rallye.detail.delete')}
          </Button>
        </Stack>
      </Stack>

      {error && <ErrorAlert message={error} />}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' },
          gap: 2,
          mb: 3,
        }}
      >
        {[
          { label: t('composition.metrics.stations'), value: metrics.stations, accent: 'primary.main' },
          { label: t('composition.metrics.challenges'), value: metrics.challenges, accent: 'tertiary.main' },
          { label: t('composition.metrics.points'), value: metrics.points, accent: 'secondary.dark' },
          { label: t('composition.metrics.duration'), value: metrics.duration, accent: 'primary.main' },
        ].map((m) => (
          <Card key={m.label} variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="caption" color="text.secondary">
                {m.label}
              </Typography>
              <Typography
                variant="h4"
                component="div"
                sx={{ fontWeight: 700, color: m.accent, lineHeight: 1.1, mt: 0.5 }}
              >
                {m.value}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack spacing={3}>
            <MultilingualField
              value={title}
              onChange={setTitle}
              label={t('rallye.create.titleLabel')}
              required
            />
            <MultilingualField
              value={description}
              onChange={setDescription}
              label={t('rallye.create.descriptionLabel')}
              richText
            />
            <Divider />
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{ justifyContent: 'space-between', alignItems: { sm: 'center' } }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={rallye.is_published}
                    onChange={handleTogglePublish}
                    disabled={togglingPublish}
                  />
                }
                label={
                  rallye.is_published ? t('rallye.detail.unpublish') : t('rallye.detail.publish')
                }
              />
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={!isDirty || saving || deTitleMissing}
              >
                {t('actions.save', { ns: 'common' })}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" component="h2">
              {t('composition.stationsAndChallenges')}
            </Typography>
            <IconButton
              color="primary"
              onClick={() => setAddOpen(true)}
              aria-label={t('station.list.addButton')}
            >
              <AddIcon />
            </IconButton>
          </Stack>

          {links.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('station.list.empty')}
            </Typography>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              modifiers={[restrictToVerticalAxis, restrictToParentElement]}
              onDragStart={(e: DragStartEvent) => setActiveStationId(String(e.active.id))}
              onDragEnd={handleDragEnd}
              onDragCancel={() => setActiveStationId(null)}
            >
              <SortableContext
                items={links.map((l) => l.station.id)}
                strategy={verticalListSortingStrategy}
              >
                <Box sx={{ opacity: reordering ? 0.7 : 1, transition: 'opacity 0.15s' }}>
                  {links.map((l, idx) => {
                    const stationChallenges = challenges[l.link_id];
                    return (
                      <SortableStationRow
                        key={l.link_id}
                        link={l}
                        index={idx}
                        label={titleOf(l.station.title) || l.station.id}
                        secondary={l.station.address ?? excerpt(titleOf(l.station.description))}
                        expanded={!!expanded[l.link_id]}
                        onToggle={() => handleToggleExpand(l)}
                        onOpen={() => navigate(`/stations/${l.station.id}`)}
                        onRemove={() => setToUnlink(l)}
                        onAddChallenge={() => setAddChallengeFor(l)}
                        dragHandleLabel={t('station.list.reorderHandle')}
                        removeLabel={t('composition.unlinkStation')}
                        expandLabel={t('composition.toggleChallenges')}
                      >
                        {stationChallenges === undefined ? (
                          <LoadingSpinner />
                        ) : stationChallenges.length === 0 ? (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ px: 2, py: 1 }}
                          >
                            {t('composition.noChallenges')}
                          </Typography>
                        ) : (
                          <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                            onDragEnd={(e) => handleChallengeDragEnd(l, e)}
                          >
                            <SortableContext
                              items={stationChallenges.map((c) => c.challenge.id)}
                              strategy={verticalListSortingStrategy}
                            >
                              <Box
                                sx={{
                                  opacity: reorderingChallenges[l.link_id] ? 0.7 : 1,
                                  transition: 'opacity 0.15s',
                                }}
                              >
                                {stationChallenges.map((c, cIdx) => {
                                  const pts =
                                    typeof c.challenge.scoring_rules?.max_points === 'number'
                                      ? c.challenge.scoring_rules.max_points
                                      : 0;
                                  const time = c.challenge.time_estimate_minutes ?? 0;
                                  return (
                                    <SortableChallengeRow
                                      key={c.link_id}
                                      link={c}
                                      index={cIdx}
                                      label={titleOf(c.challenge.title) || c.challenge.id}
                                      typeLabel={t(`challenge.types.${c.challenge.challenge_type}`)}
                                      metrics={t('composition.challengeMetrics', {
                                        points: pts,
                                        minutes: time,
                                      })}
                                      onRemove={() =>
                                        setToUnlinkChallenge({ station: l, challenge: c })
                                      }
                                      dragHandleLabel={t('station.detail.challengeReorderHandle')}
                                      removeLabel={t('composition.removeChallenge')}
                                    />
                                  );
                                })}
                              </Box>
                            </SortableContext>
                          </DndContext>
                        )}
                      </SortableStationRow>
                    );
                  })}
                </Box>
              </SortableContext>
              <DragOverlay>
                {(() => {
                  if (!activeStationId) return null;
                  const idx = links.findIndex((l) => l.station.id === activeStationId);
                  const l = links[idx];
                  if (!l) return null;
                  const isExp = !!expanded[l.link_id];
                  const chs = challenges[l.link_id];
                  // A non-interactive clone of the row so the drag preview looks
                  // exactly like the real card — icons included, and expanded
                  // stations stay expanded.
                  return (
                    <Card
                      variant="outlined"
                      sx={{ borderRadius: 2, boxShadow: 6, pointerEvents: 'none' }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1.5 }}>
                        <StationHeaderContent
                          link={l}
                          index={idx}
                          label={titleOf(l.station.title) || l.station.id}
                          secondary={l.station.address ?? excerpt(titleOf(l.station.description))}
                        />
                        <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                          <IconButton color="error" component="span">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                          <IconButton component="span">
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton component="span">
                            {isExp ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                          <IconButton component="span" sx={{ cursor: 'grabbing' }}>
                            <DragIndicatorIcon />
                          </IconButton>
                        </Stack>
                      </Box>
                      {isExp && (
                        <>
                          <Box sx={{ pb: 1, bgcolor: 'action.hover' }}>
                            {chs && chs.length > 0 ? (
                              chs.map((c, i) => (
                                <Box
                                  key={c.link_id}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    py: 0.75,
                                    pl: 4,
                                    pr: 1,
                                  }}
                                >
                                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                    <Typography variant="body2" noWrap>
                                      {`${i + 1}. ${titleOf(c.challenge.title) || c.challenge.id}`}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {t('composition.challengeMetrics', {
                                        points:
                                          typeof c.challenge.scoring_rules?.max_points === 'number'
                                            ? c.challenge.scoring_rules.max_points
                                            : 0,
                                        minutes: c.challenge.time_estimate_minutes ?? 0,
                                      })}
                                    </Typography>
                                  </Box>
                                  <Chip
                                    label={t(`challenge.types.${c.challenge.challenge_type}`)}
                                    size="small"
                                  />
                                  <IconButton component="span" color="error">
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton component="span" sx={{ cursor: 'grabbing' }}>
                                    <DragIndicatorIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              ))
                            ) : (
                              <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>
                                {t('composition.noChallenges')}
                              </Typography>
                            )}
                            <Button size="small" startIcon={<AddIcon />} sx={{ mt: 1, ml: 4 }}>
                              {t('composition.addChallenge')}
                            </Button>
                          </Box>
                        </>
                      )}
                    </Card>
                  );
                })()}
              </DragOverlay>
            </DndContext>
          )}
        </CardContent>
      </Card>

      <AddChoiceDialog
        open={addOpen}
        title={t('station.addPopup.title')}
        subtitle={t('station.addPopup.subtitle')}
        createLabel={t('station.addPopup.createNew')}
        createDescription={t('station.addPopup.createNewDesc')}
        importLabel={t('station.addPopup.importExisting')}
        importDescription={t('station.addPopup.importExistingDesc')}
        onCreateNew={() => {
          setAddOpen(false);
          navigate(`/stations/new?rallyeId=${rallyeId}`);
        }}
        onImportExisting={() => {
          setAddOpen(false);
          setImportOpen(true);
        }}
        onCancel={() => setAddOpen(false)}
      />

      <ImportStationDialog
        open={importOpen}
        linkedStationIds={links.map((l) => l.station.id)}
        onCancel={() => setImportOpen(false)}
        onImport={handleImportStation}
      />

      <AddChoiceDialog
        open={!!addChallengeFor}
        title={t('challenge.addPopup.title')}
        subtitle={t('challenge.addPopup.subtitle')}
        createLabel={t('challenge.addPopup.createNew')}
        createDescription={t('challenge.addPopup.createNewDesc')}
        importLabel={t('challenge.addPopup.importExisting')}
        importDescription={t('challenge.addPopup.importExistingDesc')}
        onCreateNew={() => {
          const target = addChallengeFor;
          setAddChallengeFor(null);
          if (target) {
            navigate(
              `/challenges/new?rallyeId=${rallyeId}&rallyeStationId=${target.link_id}`,
            );
          }
        }}
        onImportExisting={() => {
          setImportChallengeFor(addChallengeFor);
          setAddChallengeFor(null);
        }}
        onCancel={() => setAddChallengeFor(null)}
      />

      <ImportChallengeDialog
        open={!!importChallengeFor}
        stationId={importChallengeFor?.station.id}
        linkedChallengeIds={
          importChallengeFor
            ? (challenges[importChallengeFor.link_id] ?? []).map((c) => c.challenge.id)
            : []
        }
        onCancel={() => setImportChallengeFor(null)}
        onImport={handleImportChallenge}
      />

      <ConfirmDialog
        open={dialog === 'delete'}
        title={t('rallye.detail.delete')}
        message={t('rallye.detail.deleteConfirm')}
        destructive
        loading={dialogBusy}
        confirmLabel={t('actions.delete', { ns: 'common' })}
        onConfirm={handleDelete}
        onCancel={() => setDialog(null)}
      />

      <ConfirmDialog
        open={dialog === 'duplicate'}
        title={t('rallye.detail.duplicate')}
        message={t('rallye.detail.duplicateConfirm')}
        loading={dialogBusy}
        onConfirm={handleDuplicate}
        onCancel={() => setDialog(null)}
      />

      <ConfirmDialog
        open={!!toUnlink}
        title={t('composition.unlinkStation')}
        message={t('composition.unlinkStationConfirm')}
        destructive
        loading={unlinking}
        confirmLabel={t('composition.unlinkStation')}
        onConfirm={handleUnlink}
        onCancel={() => setToUnlink(null)}
      />

      <ConfirmDialog
        open={!!toUnlinkChallenge}
        title={t('composition.removeChallenge')}
        message={t('composition.removeChallengeConfirm')}
        destructive
        loading={unlinkingChallenge}
        confirmLabel={t('composition.removeChallenge')}
        onConfirm={handleUnlinkChallenge}
        onCancel={() => setToUnlinkChallenge(null)}
      />
    </Box>
  );
};

export default RallyeDetail;
